import { TFunction, useTranslation } from 'next-i18next/pages';
import { useCallback, useMemo, useRef, useState } from 'react';

import { SeoLandingPageSection } from '@pflegia/types/api/seo-landing-page';
import { useFlavorFromRoute } from '@pflegia/ui/hooks/use-flavor-from-route';
import useIntersectionObserver, { UseIntersectionObserverOptions } from '@pflegia/ui/hooks/use-intersection-observer';
import type { TestimonialProps } from '@pflegia/ui/shared/testimonial';

import { RegisterButtonTrackingIds } from '@shared/application/marketing/events/register-button';
import { useTracking } from '@shared/application/tracking.service';

export interface LandingPageSharedFunctions {
  t: TFunction;
  getTestimonialPropsFromName: (args: { name: string; videoId: string; alt?: string }) => TestimonialProps;
  signupButtonClickTrackEventRegistration: (registerButtonId: RegisterButtonTrackingIds) => void;
  headerRef: React.MutableRefObject<HTMLDivElement | null>;
  headerInView: boolean;
  sectionsVisibility: Partial<Record<SeoLandingPageSection, boolean>>;
}

const useLandingPageSharedFunctions: (namespace?: string, withSectionVisibility?: boolean) => LandingPageSharedFunctions = (
  namespace = 'landingPage',
  withSectionVisibility = false,
) => {
  const { t } = useTranslation(namespace);
  const { sourceDomainName } = useFlavorFromRoute();

  const getTestimonialPropsFromName = useCallback(
    ({ name, videoId, alt }: Pick<TestimonialProps, 'name' | 'videoId' | 'alt'>): TestimonialProps => ({
      imgSrc: `/assets/video-thumbnails/${videoId}.jpg`,
      name,
      alt,
      position: t(`testimonials.${sourceDomainName.toLowerCase()}.${videoId}.position`),
      testimonial: t(`testimonials.${sourceDomainName.toLowerCase()}.${videoId}.testimonial`),
      videoId,
    }),
    [sourceDomainName, t],
  );

  const { signupButtonClickTrackEventRegistration } = useTracking();
  const headerRef = useRef<HTMLDivElement>(null);
  const [headerInView, setHeaderInView] = useState(true);
  const handleHeaderIntersection = useCallback<UseIntersectionObserverOptions['callback']>(entries => {
    entries.forEach(entry => setHeaderInView(entry.isIntersecting));
  }, []);
  useIntersectionObserver({ callback: handleHeaderIntersection, ref: headerRef });

  const sectionsVisibility = useMemo(
    () =>
      withSectionVisibility
        ? Object.values(SeoLandingPageSection).reduce(
            (acc, section) => ({
              ...acc,
              [section]: [true, 'true'].includes(t(`${section}.isVisible`)),
            }),
            {} as Record<SeoLandingPageSection, boolean>,
          )
        : {},
    [t, withSectionVisibility],
  );

  return {
    t,
    getTestimonialPropsFromName,
    signupButtonClickTrackEventRegistration,
    headerRef,
    headerInView,
    handleHeaderIntersection,
    sectionsVisibility,
  };
};

export default useLandingPageSharedFunctions;
